import { SetStateAction, useEffect, useState } from "react";
import List from "../../components/List/List";
import {
  getApplicationVersionSignings,
  getApplicationVersionSigningsCount,
} from "../../api/versionApi";
import {
  COLUMN_TYPE,
  DEFAULT_PAGE_SIZE,
  FILTER_ACTION_TYPE,
  FILTER_INPUT_TYPE,
} from "../../constants";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Loader from "../Loader/Loader";
import ForwardButton from "../ForwardButton/ForwardButton";
import { SortProperty } from "../SortingSelector/SortingSelector";

interface SigningRequestListProps {
  applicationId: number;
  versionId: number;
}

export interface SigningRequestFilter {
  signingRequestStatusList?: string;
}

export default function SigningRequestList(props: SigningRequestListProps) {
  const [signingRequests, setSigningRequests] = useState<any>();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [numberOfSigningRequests, setNumberOfSigningRequests] = useState(-1);
  const [currentSort, setCurrentSort] = useState<SortProperty>();
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [filter, setFilter] = useState<SigningRequestFilter | null>(null);
  const [
    filterPropertySigningRequestStatusList,
    setFilterPropertySigningRequestStatusList,
  ] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setSigningRequests(null);

    getApplicationVersionSignings(
      props.versionId,
      currentPage * pageSize,
      pageSize,
      sortField,
      sortOrder,
      filter && filter.signingRequestStatusList
        ? filter.signingRequestStatusList
        : "",
      (responseData: SetStateAction<any>) => {
        let signingsWithAppendedActions: any = [];
        for (let signing of responseData) {
          signingsWithAppendedActions.push(
            Object.assign(
              {
                action: (
                  <ForwardButton
                    linkTo={
                      "/application/" +
                      props.applicationId +
                      "/version/" +
                      props.versionId +
                      "/signing/" +
                      signing.id
                    }
                  />
                ),
              },
              signing
            )
          );
        }

        if (signingsWithAppendedActions.length === 0 && currentPage > 0) {
          setCurrentPage(0);
        } else {
          setSigningRequests(signingsWithAppendedActions);
        }
      },
      (errorText: React.SetStateAction<string>) => {
        setError(errorText);
      }
    );
  }, [
    props.applicationId,
    props.versionId,
    currentPage,
    pageSize,
    sortField,
    sortOrder,
    filter,
  ]);

  useEffect(() => {
    getApplicationVersionSigningsCount(
      props.versionId,
      filter && filter.signingRequestStatusList
        ? filter.signingRequestStatusList
        : "",
      (responseData: SetStateAction<any>) => {
        setNumberOfSigningRequests(responseData);
      },
      (errorText: React.SetStateAction<string>) => {
        setError(errorText);
      }
    );
  }, [props.applicationId, props.versionId, filter]);

  const handleFilterChange = (action: string) => {
    if (action === FILTER_ACTION_TYPE.CLEAR) {
      setFilterPropertySigningRequestStatusList("");
      setFilter({});
    }
    if (action === FILTER_ACTION_TYPE.APPLY) {
      setFilter({
        signingRequestStatusList: filterPropertySigningRequestStatusList,
      });
    }
  };

  if (error) {
    return <ErrorMessage text={error} />;
  }

  return (
    <>
      {signingRequests && numberOfSigningRequests >= 0 ? (
        <List
          columns={[
            {
              propertyName: "processor",
              headerName: "Processor",
              type: COLUMN_TYPE.TEXT,
            },
            {
              propertyName: "environment",
              headerName: "Environment",
              type: COLUMN_TYPE.TEXT_TRANSLATABLE,
            },
            {
              propertyName: "status",
              headerName: "Status",
              type: COLUMN_TYPE.STATUS,
            },
            {
              propertyName: "createdOn",
              headerName: "Created On",
              type: COLUMN_TYPE.DATE,
              optional: true,
              sortable: true,
            },
            {
              propertyName: "action",
              type: COLUMN_TYPE.COMPONENT,
            },
          ]}
          items={signingRequests}
          currentPage={currentPage}
          pageSize={pageSize}
          totalNumberOfItems={numberOfSigningRequests}
          onPageChange={(page: number) => {
            setCurrentPage(page);
          }}
          onPageSizeChange={(pageSize: number) => {
            setCurrentPage(0);
            setPageSize(pageSize);
          }}
          currentSort={currentSort}
          onSortChange={(sortField: string, sortOrder: string) => {
            setCurrentSort({
              propertyName: sortField,
              type: sortOrder,
            });
            setSortField(sortField);
            setSortOrder(sortOrder);
          }}
          filterInputs={[
            {
              type: FILTER_INPUT_TYPE.MULTI_SELECT,
              propertyName: "signingRequestStatusList",
              propertyLabel: "Status",
              options: [
                {
                  name: "submitted",
                  label: "Submitted",
                  value: "SUBMITTED",
                  translateLabel: true,
                },
                {
                  name: "processing",
                  label: "Processing",
                  value: "PROCESSING",
                  translateLabel: true,
                },
                {
                  name: "processed",
                  label: "Processed",
                  value: "PROCESSED",
                  translateLabel: true,
                },
                {
                  name: "failed",
                  label: "Failed",
                  value: "FAILED",
                  translateLabel: true,
                },
              ],
              value: filterPropertySigningRequestStatusList,
              onChange: setFilterPropertySigningRequestStatusList,
            },
          ]}
          onFilterChange={(action: string) => {
            handleFilterChange(action);
          }}
        />
      ) : (
        <Loader />
      )}
    </>
  );
}
